import {
    PencilIcon,
    PlusIcon,
    TrashIcon,
    XMarkIcon,
} from "@heroicons/react/24/solid";
import { useEffect, useReducer, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import ReactTooltip from "react-tooltip";
import Breadcrumbs from "../../components/Breadcrumbs";
import InputTextModal from "../../components/clientServiceSubscriptionTask/modalGeneralComponents/InputTextModal";
import AssignedByListBox from "../../components/clientServiceSubscriptionTask/updateClientServiceSubscriptionTaskModal/AssignedByListBox";
import Header from "../../components/Header";
import ListBoxComponent from "../../components/ListboxComponent";
import Sidebar from "../../components/Sidebar";
import { usePermissions } from "../../context/PermissionsContext";
import { getRoleLabel } from "../../constants/roles";
import { getEmployees } from "../../services/getEmployees";
import { getServiceDepartments } from "../../services/service/getServiceDepartments";
import { addTeam } from "../../services/teams/addTeam";
import { getTeamRoles } from "../../services/teams/getTeamRoles";
import DepartmentListbox from "../TaskForm/DepartmentListBox";
import {
    createTeamPayload,
    getTeamRoleName,
    getTeamLeadCount,
    hasDuplicateEmployee,
    hasUnavailableTeamRole,
    isTeamLeadRole,
    normalizeTeamRoles,
    sortMembersByRole,
} from "./teamMembersUtils";

const initialState = {
    name: "",
    department: "",
    members: [],
};

const teamReducer = (state, action) => {
    switch (action.type) {
        case "SET_NAME":
            return { ...state, name: action.payload };
        case "SET_DEPARTMENT":
            return { ...state, department: action.payload };
        case "SET_MEMBERS":
            return {
                ...state,
                members: sortMembersByRole(action.payload),
            };
        case "ADD_MEMBER":
            return {
                ...state,
                members: sortMembersByRole([...state.members, action.payload]),
            };
        case "UPDATE_MEMBER":
            return {
                ...state,
                members: sortMembersByRole(
                    state.members.map((member) =>
                        member.employeeId === action.payload.originalEmployeeId
                            ? action.payload.member
                            : member
                    )
                ),
            };
        case "REMOVE_MEMBER":
            return {
                ...state,
                members: state.members.filter(
                    (member) => member.employeeId !== action.payload.employeeId
                ),
            };
        case "RESET_FORM":
            return initialState;
        default:
            throw new Error(`Unhandled action type: ${action.type}`);
    }
};

const getEmployeeDisplayName = (employee) =>
    employee?.fullName ??
    `${employee?.firstName ?? ""} ${employee?.lastName ?? ""}`.trim();

const TeamsCreatePage = () => {
    const { can, permissionsLoading } = usePermissions();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [teamRoles, setTeamRoles] = useState([]);
    const [isLoadingTeamRoles, setIsLoadingTeamRoles] = useState(true);
    const [teamRolesLoadFailed, setTeamRolesLoadFailed] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [selectedTeamRoleId, setSelectedTeamRoleId] = useState("");
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [editingMemberId, setEditingMemberId] = useState(null);
    const [editingMemberDraft, setEditingMemberDraft] = useState(null);
    const [departments, setDepartments] = useState([]);
    const [, setSelectedDepartment] = useState({});
    const [, setValidationErrors] = useState([]);
    const canWriteTeams = can("teams.write");
    const canReadTeamRoles = can("team_roles.read");
    const noPermissionMessage = "You don't have permission to do this.";
    const [state, dispatch] = useReducer(teamReducer, initialState);
    const teamRoleIds = teamRoles.map((role) => role.id).filter(Boolean);
    const renderTeamRoleName = (teamRoleId) =>
        getRoleLabel(
            teamRoles.find((role) => role.id === teamRoleId)?.name,
        ) || "Select a role";

    useEffect(() => {
        const loadTeamRoles = async () => {
            if (permissionsLoading) return;
            if (!canReadTeamRoles) {
                setTeamRoles([]);
                setSelectedTeamRoleId("");
                setTeamRolesLoadFailed(false);
                setIsLoadingTeamRoles(false);
                return;
            }

            setIsLoadingTeamRoles(true);
            setTeamRolesLoadFailed(false);
            const response = await getTeamRoles();
            if (response?.success && response?.data?.success !== false) {
                const roles = normalizeTeamRoles(response?.data?.teamRoles);
                const defaultRole = roles.find((role) => role.isTeamLead === true);
                setTeamRoles(roles);
                setSelectedTeamRoleId(defaultRole?.id ?? "");
            } else {
                setTeamRoles([]);
                setSelectedTeamRoleId("");
                setTeamRolesLoadFailed(true);
                toast.error(
                    response?.status === 403
                        ? noPermissionMessage
                        : response?.data?.message ??
                              response?.message ??
                              "Unable to load team roles."
                );
            }
            setIsLoadingTeamRoles(false);
        };

        loadTeamRoles();
    }, [canReadTeamRoles, permissionsLoading]);

    useEffect(() => {
        const getNewEmployees = async () => {
            const response = await getEmployees();
            setEmployees(response.data.employees);
        };

        getNewEmployees();
    }, []);

    useEffect(() => {
        const getNewDepartments = async () => {
            const response = await getServiceDepartments();
            if (response.success) {
                setDepartments(response.data.departments);
                setSelectedDepartment(response.data.departments[0]);
            } else {
                toast.error(response.data.message);
            }
        };

        getNewDepartments();
    }, []);

    const validateForm = () => {
        const newErrors = [];

        if (!state.name) newErrors.push("Team name is required.");
        if (!state.department) newErrors.push("Department is required.");
        if (state.members.length === 0)
            newErrors.push("At least one member is required.");
        if (hasDuplicateEmployee(state.members))
            newErrors.push("Employee is already a member of the team.");
        if (hasUnavailableTeamRole(state.members, teamRoles))
            newErrors.push(
                "One or more team members have a role that is no longer available. Please select a new role."
            );

        const teamLeadCount = getTeamLeadCount(state.members, teamRoles);
        if (teamLeadCount !== 1)
            newErrors.push("Each team must have exactly one Team Lead.");

        setValidationErrors(newErrors);
        if (newErrors.length > 0) newErrors.forEach((error) => toast.error(error));
        return newErrors.length === 0;
    };

    const resetAddMemberForm = () => {
        setSelectedEmployee(null);
        setSelectedTeamRoleId(
            teamRoles.find((role) => role.isTeamLead === true)?.id ?? ""
        );
    };

    const resetEditingState = () => {
        setEditingMemberId(null);
        setEditingMemberDraft(null);
    };

    const onAddTeamMember = () => {
        if (!canWriteTeams || !canReadTeamRoles || teamRolesLoadFailed) {
            toast.error(noPermissionMessage);
            return;
        }

        const selectedRole = teamRoles.find(
            (role) => role.id === selectedTeamRoleId
        );

        if (!selectedEmployee || !selectedRole) {
            toast.error("Please select a team member and role.");
            return;
        }

        if (
            state.members.some(
                (member) => member.employeeId === selectedEmployee.employeeId
            )
        ) {
            toast.error("Employee is already a member of the team.");
            return;
        }

        if (
            isTeamLeadRole(
                {
                    teamRoleId: selectedRole.id,
                    roleName: selectedRole.name,
                },
                teamRoles
            ) &&
            getTeamLeadCount(state.members, teamRoles) >= 1
        ) {
            toast.error("Each team must have exactly one Team Lead.");
            return;
        }

        dispatch({
            type: "ADD_MEMBER",
            payload: {
                ...selectedEmployee,
                fullName: getEmployeeDisplayName(selectedEmployee),
                teamRoleId: selectedRole.id,
                roleName: selectedRole.name,
            },
        });

        resetAddMemberForm();
    };

    const onClickEditMember = (member) => {
        if (!canWriteTeams) {
            toast.error(noPermissionMessage);
            return;
        }

        setEditingMemberId(member.employeeId);
        setEditingMemberDraft({
            employee: employees.find(
                (employee) => employee.employeeId === member.employeeId
            ) ?? member,
            teamRoleId: member.teamRoleId,
            roleName: getTeamRoleName(member, teamRoles),
        });
    };

    const onSaveEditedMember = () => {
        if (!canWriteTeams) {
            toast.error(noPermissionMessage);
            return;
        }

        if (
            !editingMemberId ||
            !editingMemberDraft?.employee ||
            !editingMemberDraft?.teamRoleId
        ) {
            toast.error("Please select a team member and role.");
            return;
        }

        const duplicateEmployee = state.members.some(
            (member) =>
                member.employeeId !== editingMemberId &&
                member.employeeId === editingMemberDraft.employee.employeeId
        );

        if (duplicateEmployee) {
            toast.error("Employee is already a member of the team.");
            return;
        }

        const anotherTeamLeadExists = state.members.some(
            (member) =>
                member.employeeId !== editingMemberId &&
                isTeamLeadRole(member, teamRoles)
        );

        if (
            isTeamLeadRole(editingMemberDraft, teamRoles) &&
            anotherTeamLeadExists
        ) {
            toast.error("Each team must have exactly one Team Lead.");
            return;
        }

        dispatch({
            type: "UPDATE_MEMBER",
            payload: {
                originalEmployeeId: editingMemberId,
                member: {
                    ...editingMemberDraft.employee,
                    fullName: getEmployeeDisplayName(editingMemberDraft.employee),
                    teamRoleId: editingMemberDraft.teamRoleId,
                    roleName: getTeamRoleName(editingMemberDraft, teamRoles),
                },
            },
        });

        resetEditingState();
    };

    const onSave = async () => {
        if (isSaving) return;

        if (
            !canWriteTeams ||
            !canReadTeamRoles ||
            teamRolesLoadFailed
        ) {
            toast.error(noPermissionMessage);
            return;
        }

        if (validateForm()) {
            setIsSaving(true);
            try {
                const response = await addTeam(createTeamPayload(state));

                if (response?.success && response?.data?.success) {
                    toast.success("Team created successfully.");
                    dispatch({ type: "RESET_FORM" });
                    resetAddMemberForm();
                    resetEditingState();
                } else {
                    toast.error(response?.message ?? "Unable to create team.");
                }
            } finally {
                setIsSaving(false);
                setValidationErrors([]);
            }
        }
    };

    return (
        <section className="flex flex-col w-full">
            <Toaster />
            <ReactTooltip
                type="light"
                effect="solid"
                place="top"
                border={true}
                borderColor={"rgb(220,220,220)"}
                className="rounded-md bg-gray-50"
                multiline={false}
            />
            <div className="flex h-screen overflow-hidden">
                <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
                <div className="relative flex flex-col flex-1 overflow-x-hidden overflow-y-auto">
                    <Header
                        sidebarOpen={sidebarOpen}
                        setSidebarOpen={setSidebarOpen}
                    />

                    <main>
                        <div className="w-full px-4 py-8 mx-auto sm:px-6 lg:px-8 max-w-9xl">
                            <div className="flex flex-col items-start max-w-2xl">
                                <Breadcrumbs
                                    home="/teams"
                                    pages={[
                                        {
                                            name: "Teams",
                                            href: `/teams`,
                                            current: true,
                                        },
                                    ]}
                                />

                                <h1 className="p-4 text-2xl font-bold md:text-3xl text-slate-800">
                                    Create Team
                                </h1>
                                {!permissionsLoading && !canReadTeamRoles && (
                                    <div className="w-full p-4 text-sm border rounded-sm border-slate-200 text-slate-600">
                                        You don't have permission to view team roles.
                                    </div>
                                )}
                                {canReadTeamRoles && isLoadingTeamRoles && (
                                    <div className="w-full p-4 text-sm border rounded-sm border-slate-200 text-slate-600">
                                        Loading team roles...
                                    </div>
                                )}
                                {canReadTeamRoles &&
                                    !isLoadingTeamRoles &&
                                    teamRolesLoadFailed && (
                                        <div className="w-full p-4 text-sm border rounded-sm border-slate-200 text-slate-600">
                                            Team roles could not be loaded. Please try again.
                                        </div>
                                    )}
                                {canReadTeamRoles &&
                                    !isLoadingTeamRoles &&
                                    !teamRolesLoadFailed && (
                                    <>
                                <div className="relative w-full pb-4 bg-white border rounded-sm shadow-lg border-slate-200">
                                    <div className="px-5 py-4 space-y-5">
                                        <InputTextModal
                                            required={true}
                                            label="Team name *"
                                            name="name"
                                            onChange={(e) =>
                                                dispatch({
                                                    type: "SET_NAME",
                                                    payload: e.target.value,
                                                })
                                            }
                                            value={state.name}
                                        />

                                        <DepartmentListbox
                                            label={"Department *"}
                                            departments={departments}
                                            selectedDepartment={state.department}
                                            onChangeDepartment={(e) =>
                                                dispatch({
                                                    type: "SET_DEPARTMENT",
                                                    payload: e,
                                                })
                                            }
                                        />

                                        {state.members.length > 0 ? (
                                            <div>
                                                <p className="py-4 text-sm font-medium text-gray-700">
                                                    Team members list
                                                </p>

                                                <div className="overflow-x-auto">
                                                    <table className="min-w-full bg-white">
                                                        <thead>
                                                            <tr>
                                                                <th className="px-4 py-2 text-sm font-medium text-left text-gray-700 border-b border-gray-200">
                                                                    Name
                                                                </th>
                                                                <th className="px-4 py-2 text-sm font-medium text-left text-gray-700 border-b border-gray-200">
                                                                    Role
                                                                </th>
                                                                <th className="px-4 py-2 text-sm font-medium text-left text-gray-700 border-b border-gray-200">
                                                                    Actions
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {state.members.map((member) => {
                                                                const isEditing =
                                                                    editingMemberId === member.employeeId;

                                                                return (
                                                                    <tr key={member.employeeId}>
                                                                        <td className="px-4 py-3 text-sm text-gray-700 border-b border-gray-200 min-w-[260px]">
                                                                            {isEditing ? (
                                                                                <AssignedByListBox
                                                                                    label="Team member"
                                                                                    employees={employees}
                                                                                    assignedBy={
                                                                                        editingMemberDraft?.employee ??
                                                                                        member
                                                                                    }
                                                                                    onChange={(employee) =>
                                                                                        setEditingMemberDraft(
                                                                                            (currentDraft) => ({
                                                                                                ...currentDraft,
                                                                                                employee,
                                                                                            })
                                                                                        )
                                                                                    }
                                                                                />
                                                                            ) : (
                                                                                getEmployeeDisplayName(member)
                                                                            )}
                                                                        </td>
                                                                        <td className="px-4 py-3 text-sm text-gray-700 border-b border-gray-200 min-w-[220px]">
                                                                            {isEditing ? (
                                                                                <ListBoxComponent
                                                                                    title="Role"
                                                                                    name={`role-${member.employeeId}`}
                                                                                    items={teamRoleIds}
                                                                                    selectedItem={
                                                                                        editingMemberDraft?.teamRoleId ??
                                                                                        member.teamRoleId ??
                                                                                        ""
                                                                                    }
                                                                                    setSelectedItem={(teamRoleId) => {
                                                                                        const role = teamRoles.find(
                                                                                            (teamRole) =>
                                                                                                teamRole.id === teamRoleId
                                                                                        );
                                                                                        setEditingMemberDraft(
                                                                                            (currentDraft) => ({
                                                                                                ...currentDraft,
                                                                                                teamRoleId: role?.id ?? "",
                                                                                                roleName: role?.name ?? "",
                                                                                            })
                                                                                        );
                                                                                    }}
                                                                                    renderItem={renderTeamRoleName}
                                                                                />
                                                                            ) : (
                                                                                getRoleLabel(
                                                                                    getTeamRoleName(
                                                                                        member,
                                                                                        teamRoles,
                                                                                    ),
                                                                                )
                                                                            )}
                                                                        </td>
                                                                        <td className="px-4 py-3 text-sm text-gray-700 border-b border-gray-200">
                                                                            <div className="flex items-center gap-2">
                                                                                {isEditing ? (
                                                                                    <>
                                                                                        <span
                                                                                            data-tip={
                                                                                                canWriteTeams
                                                                                                    ? "Save"
                                                                                                    : noPermissionMessage
                                                                                            }
                                                                                            className="inline-flex"
                                                                                        >
                                                                                            <button
                                                                                                type="button"
                                                                                                onClick={
                                                                                                    onSaveEditedMember
                                                                                                }
                                                                                                disabled={
                                                                                                    !canWriteTeams
                                                                                                }
                                                                                                className={`inline-flex items-center rounded-sm px-3 py-2 text-sm font-medium ${
                                                                                                    canWriteTeams
                                                                                                        ? "text-teal-700 hover:bg-teal-50"
                                                                                                        : "text-slate-400 cursor-not-allowed"
                                                                                                }`}
                                                                                            >
                                                                                                Save
                                                                                            </button>
                                                                                        </span>
                                                                                        <span
                                                                                            data-tip="Cancel"
                                                                                            className="inline-flex"
                                                                                        >
                                                                                            <button
                                                                                                type="button"
                                                                                                onClick={
                                                                                                    resetEditingState
                                                                                                }
                                                                                                className="inline-flex items-center rounded-sm p-2 text-slate-500 hover:bg-slate-100"
                                                                                            >
                                                                                                <XMarkIcon className="w-5 h-5" />
                                                                                            </button>
                                                                                        </span>
                                                                                    </>
                                                                                ) : (
                                                                                    <>
                                                                                        <span
                                                                                            data-tip={
                                                                                                canWriteTeams
                                                                                                    ? "Edit"
                                                                                                    : noPermissionMessage
                                                                                            }
                                                                                            className="inline-flex"
                                                                                        >
                                                                                            <button
                                                                                                type="button"
                                                                                                onClick={() =>
                                                                                                    onClickEditMember(
                                                                                                        member
                                                                                                    )
                                                                                                }
                                                                                                disabled={
                                                                                                    !canWriteTeams
                                                                                                }
                                                                                                className={`inline-flex items-center rounded-sm p-2 ${
                                                                                                    canWriteTeams
                                                                                                        ? "text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                                                                                                        : "text-slate-400 cursor-not-allowed"
                                                                                                }`}
                                                                                            >
                                                                                                <PencilIcon className="w-5 h-5" />
                                                                                            </button>
                                                                                        </span>
                                                                                        <span
                                                                                            data-tip={
                                                                                                canWriteTeams
                                                                                                    ? "Delete"
                                                                                                    : noPermissionMessage
                                                                                            }
                                                                                            className="inline-flex"
                                                                                        >
                                                                                            <button
                                                                                                type="button"
                                                                                                onClick={() =>
                                                                                                    dispatch({
                                                                                                        type: "REMOVE_MEMBER",
                                                                                                        payload: member,
                                                                                                    })
                                                                                                }
                                                                                                disabled={
                                                                                                    !canWriteTeams
                                                                                                }
                                                                                                className={`inline-flex items-center rounded-sm p-2 ${
                                                                                                    canWriteTeams
                                                                                                        ? "text-red-500 hover:bg-red-50 hover:text-red-700"
                                                                                                        : "text-slate-400 cursor-not-allowed"
                                                                                                }`}
                                                                                            >
                                                                                                <TrashIcon className="w-5 h-5" />
                                                                                            </button>
                                                                                        </span>
                                                                                    </>
                                                                                )}
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        ) : (
                                            <p className="py-4 font-medium text-red-500 text-medium">
                                                Please add at least one team member.
                                            </p>
                                        )}

                                        <div className="flex w-full">
                                            <div className="w-1/2">
                                                <AssignedByListBox
                                                    label="Team member"
                                                    employees={employees}
                                                    assignedBy={selectedEmployee}
                                                    onChange={(employee) =>
                                                        setSelectedEmployee(employee)
                                                    }
                                                />
                                            </div>
                                            <div className="pl-4 w-1/2">
                                                <ListBoxComponent
                                                    title="Role"
                                                    name="newTeamMemberRole"
                                                    items={teamRoleIds}
                                                    selectedItem={selectedTeamRoleId}
                                                    setSelectedItem={setSelectedTeamRoleId}
                                                    disabled={isLoadingTeamRoles}
                                                    renderItem={renderTeamRoleName}
                                                />
                                            </div>
                                        </div>
                                        <div className="flex justify-end w-full">
                                            <span
                                                data-tip={
                                                    canWriteTeams
                                                        ? "Add Team member"
                                                        : noPermissionMessage
                                                }
                                                className="inline-flex"
                                            >
                                                <button
                                                    onClick={onAddTeamMember}
                                                    disabled={!canWriteTeams}
                                                    className={`z-50 inline-flex justify-end px-4 py-2 mt-2 text-sm font-medium text-white bg-teal-500 border border-transparent rounded-sm shadow-sm ${
                                                        canWriteTeams
                                                            ? "hover:bg-teal-600"
                                                            : "opacity-50 cursor-not-allowed"
                                                    } focus:outline-none focus:ring-0`}
                                                >
                                                    <PlusIcon className="w-5 h-5 mr-2" />
                                                    Team member
                                                </button>
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex justify-end w-full">
                                    <span
                                        data-tip={
                                            canWriteTeams
                                                ? "Save Team"
                                                : noPermissionMessage
                                        }
                                        className="inline-flex"
                                    >
                                        <button
                                            onClick={onSave}
                                            disabled={!canWriteTeams || isSaving}
                                            className={`z-50 inline-flex justify-end px-4 py-2 mt-8 text-sm font-medium text-white bg-teal-500 border border-transparent rounded-sm shadow-sm ${
                                                canWriteTeams && !isSaving
                                                    ? "hover:bg-teal-600"
                                                    : "opacity-50 cursor-not-allowed"
                                            } focus:outline-none focus:ring-0`}
                                        >
                                            {isSaving ? "Saving..." : "Save Team"}
                                        </button>
                                    </span>
                                </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </main>
                </div>
            </div>
        </section>
    );
};

export default TeamsCreatePage;
